import { useState } from "react";
import { Link } from "react-router";
import { subscribeToBrevo } from "../services/subscribeToBrevo";
import { trackEvent } from "../services/analytics";
import { devError } from "../utils/devLog";

export default function SubscribeForm({
  userType = "candidat",
  onSuccess,
  compact = false,
}) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [company, setCompany] = useState("");
  const [gdprAccepted, setGdprAccepted] = useState(false);
  const [status, setStatus] = useState("");
  const isCandidat = userType === "candidat";

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!gdprAccepted) {
      setStatus("Tu dois accepter la politique de confidentialité.");
      return;
    }

    setStatus("Envoi en cours...");
    trackEvent("form_submit", { user_type: userType });

    try {
      await subscribeToBrevo({
        email,
        firstName,
        userType,
        company: isCandidat ? undefined : company,
      });

      const successMessage = isCandidat
        ? "Merci ! Tu seras prévenu du lancement."
        : "Merci ! Vous serez prévenu du lancement.";

      setStatus(successMessage);
      trackEvent("form_success", { user_type: userType });
      setEmail("");
      setFirstName("");
      setCompany("");
      setGdprAccepted(false);
      onSuccess?.();
    } catch (err) {
      devError(err);
      const apiMessage =
        err.response?.data?.message || err.response?.data?.error;
      const errorMessage =
        apiMessage ||
        (err.response?.status === 429
          ? "Trop de tentatives. Réessaie dans quelques minutes."
          : err.response?.status === 400
            ? "Vérifie ton email et réessaie."
            : err.response?.status >= 500
              ? "Serveur indisponible. Réessaie dans quelques instants."
              : "Une erreur réseau est survenue. Réessaie.");
      setStatus(errorMessage);
      trackEvent("form_error", { user_type: userType });
    }
  };

  const inputClass = compact
    ? "w-full px-4 py-2.5 text-base sm:text-sm text-gray-900 placeholder:text-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary"
    : "w-full px-4 py-2 text-gray-900 placeholder:text-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary";

  return (
    <div className="w-full">
      {!compact && (
        <h2
          id="modal-title"
          className="text-xl font-semibold mb-4 text-gray-900 text-center"
        >
          {isCandidat ? "Rejoins la liste d'attente" : "Pré-inscription entreprise"}
        </h2>
      )}
      <form onSubmit={handleSubmit} className={`flex flex-col ${compact ? "gap-3" : "gap-4"}`}>
        <input
          type="text"
          name="firstName"
          required
          placeholder={isCandidat ? "Ton prénom" : "Votre prénom"}
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          className={inputClass}
        />
        {!isCandidat && (
          <input
            type="text"
            name="company"
            required
            placeholder="Nom de votre entreprise"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            className={inputClass}
          />
        )}
        <input
          type="email"
          name="email"
          required
          placeholder={
            isCandidat ? "Ton email" : "Votre email professionnel"
          }
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={inputClass}
        />
        <label className="flex items-start gap-2 text-xs sm:text-sm text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={gdprAccepted}
            onChange={(e) => setGdprAccepted(e.target.checked)}
            required
            className="mt-0.5 flex-shrink-0"
          />
          <span>
            J'accepte la{" "}
            <Link
              to="/politique-de-confidentialite"
              className="text-brand-primary underline hover:opacity-80"
              target="_blank"
            >
              politique de confidentialité
            </Link>
            .
          </span>
        </label>
        <button
          type="submit"
          className={`text-white font-semibold rounded-md transition ${
            compact ? "py-2.5 text-base sm:text-sm" : "py-3"
          } ${
            isCandidat
              ? "bg-gradient-to-r from-brand-primary to-brand-accent1 hover:opacity-90"
              : "bg-gradient-to-r from-brand-primaryDark to-brand-accent2 hover:opacity-90"
          }`}
        >
          {isCandidat ? "Rejoins la liste d'attente" : "Pré-inscription entreprise"}
        </button>
      </form>
      {status && (
        <p
          className={`text-sm mt-3 text-center ${
            status.includes("erreur") ||
            status.includes("Erreur") ||
            status.includes("Vérifie") ||
            status.includes("indisponible") ||
            status.includes("réseau") ||
            status.includes("accepter") ||
            status.includes("indisponible") ||
            status.includes("Trop de tentatives") ||
            status.includes("obligatoire") ||
            status.includes("invalide") ||
            status.includes("Configuration")
              ? "text-red-600"
              : status === "Envoi en cours..."
                ? "text-gray-600"
                : "text-green-700"
          }`}
        >
          {status}
        </p>
      )}
      {!compact && (
        <p className="text-xs text-gray-500 mt-3 text-center">
          {isCandidat
            ? "Aucune pub. Juste un mail pour te prévenir du lancement."
            : "Aucune pub. Un conseiller vous recontactera pour organiser votre démo."}
        </p>
      )}
    </div>
  );
}
